import { useState } from 'react';
import { Link } from 'wouter';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, BarChart2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Reveal, formatGBP, useSavingsCalc, EASE } from './helpers';

export const SavingsCalculator = () => {
    const reduce = useReducedMotion();
    const [headcount, setHeadcount] = useState(500);
    const [avgSalary, setAvgSalary] = useState(75000);
    const [turnover, setTurnover] = useState(15);
    const [calculated, setCalculated] = useState(false);

    const calc = useSavingsCalc(headcount, avgSalary, turnover);

    return (
        <section id="savings" className="py-32 scroll-mt-16 border-t border-border/40 bg-muted/20">
            <div className="max-w-6xl mx-auto px-6">
                <Reveal className="max-w-2xl mb-14">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">The business case</p>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                        What is losing your best people
                        <span className="text-muted-foreground/60"> already costing you?</span>
                    </h2>
                    <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                        A rough model of what every key departure costs you. For the full CFO-grade case, see the{' '}
                        <Link href="/calculator/advanced" className="underline underline-offset-2 hover:text-foreground">
                            advanced calculator
                        </Link>
                        .
                    </p>
                </Reveal>

                <Reveal>
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                        <div className="lg:col-span-3 rounded-2xl border border-border/60 bg-card p-8">
                            <div className="space-y-9">
                                <div>
                                    <div className="flex justify-between items-baseline mb-3">
                                        <label className="text-sm text-muted-foreground">Total headcount</label>
                                        <span className="text-sm font-semibold tabular-nums">{headcount.toLocaleString()}</span>
                                    </div>
                                    <Slider min={50} max={5000} step={50} value={[headcount]} onValueChange={([v]) => setHeadcount(v)} />
                                    <div className="flex justify-between text-xs text-muted-foreground/60 mt-1.5">
                                        <span>50</span>
                                        <span>5,000</span>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex justify-between items-baseline mb-3">
                                        <label className="text-sm text-muted-foreground">Average salary of who's leaving</label>
                                        <span className="text-sm font-semibold tabular-nums">{formatGBP(avgSalary)}</span>
                                    </div>
                                    <Slider min={30000} max={200000} step={5000} value={[avgSalary]} onValueChange={([v]) => setAvgSalary(v)} />
                                    <div className="flex justify-between text-xs text-muted-foreground/60 mt-1.5">
                                        <span>£30k</span>
                                        <span>£200k</span>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex justify-between items-baseline mb-3">
                                        <label className="text-sm text-muted-foreground">Turnover per year</label>
                                        <span className="text-sm font-semibold tabular-nums">{turnover}%</span>
                                    </div>
                                    <Slider min={5} max={40} step={1} value={[turnover]} onValueChange={([v]) => setTurnover(v)} />
                                    <div className="flex justify-between text-xs text-muted-foreground/60 mt-1.5">
                                        <span>5%</span>
                                        <span>40%</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground/70 mt-2">{calc.exits} departures per year</p>
                                </div>
                            </div>

                            <div className="mt-8 pt-6 border-t border-border/50 grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <div className="text-muted-foreground mb-1 text-xs">Exits per year</div>
                                    <div className="font-semibold tabular-nums">{calc.exits}</div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground mb-1 text-xs">Cost per exit</div>
                                    <div className="font-semibold tabular-nums">{formatGBP(calc.costPerExit)}</div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground mb-1 text-xs">Cost per year</div>
                                    <div className="font-semibold tabular-nums">{formatGBP(calc.totalAnnualCost)}</div>
                                </div>
                            </div>

                            <Button className="w-full mt-6 rounded-full h-11" onClick={() => setCalculated(true)}>
                                Calculate <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>

                        <div
                            className="lg:col-span-2 rounded-2xl p-8 flex flex-col justify-center relative overflow-hidden"
                            style={{ background: 'linear-gradient(160deg, hsl(210 71% 14%) 0%, hsl(221 83% 22%) 100%)' }}
                        >
                            <motion.div
                                className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-30"
                                style={{ background: 'radial-gradient(circle, hsl(221 83% 53%) 0%, transparent 70%)' }}
                                animate={reduce ? undefined : { scale: [1, 1.15, 1] }}
                                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                            />
                            <div className="relative">
                                <AnimatePresence mode="wait">
                                    {calculated ? (
                                        <motion.div
                                            key="result"
                                            initial={{ opacity: 0, y: 16 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.5, ease: EASE }}
                                            className="text-white"
                                        >
                                            <p className="text-xs text-white/60 mb-2 uppercase tracking-[0.2em] font-semibold">Estimated saving per year</p>
                                            <div className="text-5xl font-bold mb-8 tabular-nums tracking-tight" data-testid="text-savings">
                                                {formatGBP(calc.savings)}
                                            </div>
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center py-2.5 border-b border-white/10 text-sm">
                                                    <span className="text-white/70">Onboarding days saved per hire</span>
                                                    <span className="font-semibold text-white tabular-nums">{calc.rampDaysSaved} days</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2.5 border-b border-white/10 text-sm">
                                                    <span className="text-white/70">Payback</span>
                                                    <span className="font-semibold text-white tabular-nums">
                                                        {calc.payback} {calc.payback === 1 ? 'month' : 'months'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center py-2.5 text-sm">
                                                    <span className="text-white/70">ROI multiple</span>
                                                    <span className="font-semibold text-white tabular-nums">{(calc.savings / 50000).toFixed(1)}×</span>
                                                </div>
                                            </div>
                                            <Link href="/calculator/advanced">
                                                <Button variant="outline" className="w-full mt-7 text-white border-white/30 hover:bg-white/10 hover:text-white rounded-full h-11">
                                                    Build the full case
                                                </Button>
                                            </Link>
                                        </motion.div>
                                    ) : (
                                        <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-white">
                                            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                                                <BarChart2 className="h-6 w-6 text-white/60" />
                                            </div>
                                            <p className="text-white/60 text-sm">
                                                Move the sliders,
                                                <br />
                                                then hit calculate.
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
